<script setup>
import {
  defineProps,
  computed,
  reactive,
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import ConversationDateSeparator from 'dashboard/components/ConversationDateSeparator.vue';
import ConversationDateNavigator from 'dashboard/components/ConversationDateNavigator.vue';
import {
  format,
  startOfDay,
  subDays,
  subMonths,
  startOfMonth,
  fromUnixTime,
} from 'date-fns';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: [Number, String],
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  inboxSupportsEdit: {
    type: Boolean,
    default: false,
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const { locale } = useI18n();
const store = useStore();

const listEl = ref(null);
const currentDateKey = ref(null);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const currentChat = useMapGetter('getSelectedChat');

const isGroupConversation = computed(
  () => currentChat.value?.group_type === 'group'
);

const fetchedReplyMessages = reactive(new Map());

const fetchReplyMessage = async (messageId, conversationId) => {
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null;
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};

function dateKeyFromTimestamp(ts) {
  return format(startOfDay(fromUnixTime(ts)), 'yyyy-MM-dd');
}

function formatDateLabel(dateKey) {
  const date = new Date(dateKey + 'T00:00:00');
  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

const messageDateKeys = computed(() => {
  return allMessages.value.map(m =>
    m.createdAt ? dateKeyFromTimestamp(m.createdAt) : null
  );
});

function needsSeparatorBefore(index) {
  if (index === 0) return true;
  return messageDateKeys.value[index] !== messageDateKeys.value[index - 1];
}

const currentDateItem = computed(() => {
  if (!currentDateKey.value) {
    const last = messageDateKeys.value[messageDateKeys.value.length - 1];
    if (!last) return null;
    return { dateKey: last, label: formatDateLabel(last) };
  }
  return {
    dateKey: currentDateKey.value,
    label: formatDateLabel(currentDateKey.value),
  };
});

function updateCurrentDate() {
  if (!listEl.value) return;
  const separators = listEl.value.querySelectorAll('[data-date-key]');
  const containerTop = listEl.value.getBoundingClientRect().top;
  let active = null;
  separators.forEach(el => {
    if (el.getBoundingClientRect().top - containerTop <= 50) {
      active = el.dataset.dateKey;
    }
  });
  if (active) currentDateKey.value = active;
}

onMounted(() => {
  listEl.value?.addEventListener('scroll', updateCurrentDate, {
    passive: true,
  });
});

onBeforeUnmount(() => {
  listEl.value?.removeEventListener('scroll', updateCurrentDate);
});

function scrollToDateKey(dateKey) {
  if (!listEl.value) return;
  const el = listEl.value.querySelector(`[data-date-key="${dateKey}"]`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function getFirstLoadedDateKey() {
  return messageDateKeys.value.find(k => k != null) ?? null;
}

async function fetchAroundDate(dateKey) {
  if (!currentChat.value?.id) return;
  await store.dispatch('fetchMessagesAroundDate', {
    conversationId: currentChat.value.id,
    date: dateKey,
  });
  await nextTick();
}

async function ensureDateLoaded(dateKey) {
  if (messageDateKeys.value.includes(dateKey)) return;
  await fetchAroundDate(dateKey);
}

async function navigateToday() {
  const key = format(startOfDay(new Date()), 'yyyy-MM-dd');
  await ensureDateLoaded(key);
  scrollToDateKey(key);
}

async function navigateYesterday() {
  const key = format(startOfDay(subDays(new Date(), 1)), 'yyyy-MM-dd');
  await ensureDateLoaded(key);
  scrollToDateKey(key);
}

async function navigateLastWeek() {
  const key = format(startOfDay(subDays(new Date(), 7)), 'yyyy-MM-dd');
  await ensureDateLoaded(key);
  const keys = [...new Set(messageDateKeys.value.filter(Boolean))].sort();
  const target = keys.find(k => k >= key) ?? keys[0];
  if (target) scrollToDateKey(target);
}

async function navigateLastMonth() {
  const key = format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd');
  await ensureDateLoaded(key);
  const keys = [...new Set(messageDateKeys.value.filter(Boolean))].sort();
  const target = keys.find(k => k >= key) ?? keys[0];
  if (target) scrollToDateKey(target);
}

async function navigateFirst() {
  await fetchAroundDate('1970-01-01');
  const key = getFirstLoadedDateKey();
  if (key) scrollToDateKey(key);
}

async function navigateDate(dateStr) {
  await ensureDateLoaded(dateStr);
  const keys = [...new Set(messageDateKeys.value.filter(Boolean))].sort();
  const target = keys.find(k => k >= dateStr) ?? keys[keys.length - 1];
  if (target) scrollToDateKey(target);
}
</script>

<template>
  <ul ref="listEl" class="px-4 bg-n-surface-1">
    <li
      class="sticky top-0 z-10 py-1.5 flex justify-center pointer-events-none"
    >
      <ConversationDateNavigator
        :current-date-item="currentDateItem"
        class="pointer-events-auto"
        @navigate-today="navigateToday"
        @navigate-yesterday="navigateYesterday"
        @navigate-last-week="navigateLastWeek"
        @navigate-last-month="navigateLastMonth"
        @navigate-first="navigateFirst"
        @navigate-date="navigateDate"
      />
    </li>
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <ConversationDateSeparator
        v-if="needsSeparatorBefore(index) && messageDateKeys[index]"
        :data-date-key="messageDateKeys[index]"
        :label="formatDateLabel(messageDateKeys[index])"
      />
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, allMessages)"
        :group-with-previous="
          index > 0 && shouldGroupWithNext(index - 1, allMessages)
        "
        :is-group-conversation="isGroupConversation"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :inbox-supports-edit="inboxSupportsEdit"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
